"use client";

import { ElectronicContractModel, ElectronicContractModelFactory } from "@/core/models/electronic-contract.model";
import { ElectronicContractRepo } from "@/core/repos/tables/electronic-contract.repo";
import container from "@/lib/di/container";
import { useState, useEffect, Suspense } from "react";
import { Button, Dropdown, message, Tooltip } from "antd";
import {
    EyeOutlined,
    SafetyOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined,
    MoreOutlined,
} from "@ant-design/icons";
import { useTitleDataStore } from "@/core/stores/title-data.store";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import { DetailModal } from "./modals/DetailModal";
import { ViewDocumentModal } from "./modals/ViewDocumentModal";
import { SendOtpModal } from "./modals/SendOtpModal";
import { SignDocumentModal } from "./modals/SignDocumentModal";
import { CreateElectronicContractMultiModal } from "./modals/CreateElectronicContractMultiModal";
import {
    FilterInputTemplate,
    FilterInputType,
    FilterBar,
} from "@/app/home/components/filter/FilterBar";
import {
    DataTable,
    FilterSearchPayload,
} from "@/app/home/components/table/DataTable";
import { DeleteButton } from "@/app/home/components/table/DeleteButton";
import { Tag } from "antd";

const electronicContractRepo = container.get(ElectronicContractRepo);

function ElectronicContract() {
    const titleData = useTitleDataStore();
    const [modalData, setModalData] = useState<ElectronicContractModel>(
        ElectronicContractModelFactory.getEmpty()
    );
    const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(
        new Map()
    );
    const [selectedRows, setSelectedRows] = useState<ElectronicContractModel[]>([]);
    const [isDetailModal, setDetailModal] = useState(false);
    const [isCreateMultiModal, setCreateMultiModal] = useState(false);
    const [isViewDocumentModal, setViewDocumentModal] = useState(false);
    const [isSendOtpModal, setSendOtpModal] = useState(false);
    const [isSignDocumentModal, setSignDocumentModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [filterTemplates] = useState<FilterInputTemplate[]>(
        [
            {
                dataIndex: "search",
                label: "Tìm kiếm",
                placeholder: "Nhập từ khóa tìm kiếm",
                type: FilterInputType.text,
            },
            {
                dataIndex: "waterUserCode",
                label: "Mã khách hàng",
                placeholder: "Nhập mã khách hàng",
                type: FilterInputType.text,
            },
            {
                dataIndex: "status",
                label: "Trạng thái",
                placeholder: "Chọn trạng thái",
                type: FilterInputType.select,
                options: [
                    { name: "Đã ký", value: "Signed" },
                    { name: "Chờ ký", value: "Pending" },
                    { name: "Nháp", value: "Draft" },
                ],
            },
        ]
    );

    const showDetail = (record: ElectronicContractModel) => {
        setModalData(record);
        setDetailModal(true);
    };

    useEffect(() => {
        titleData.setTitleActions({
            text: "Tạo hợp đồng điện tử",
            onPress: () => {
                setCreateMultiModal(true);
            },
        });
    }, [titleData]);

    async function onFilterChange(
        map: Map<string, FilterSearchPayload>,
        changes: string[]
    ) {
        setFilterMap(new Map(map));
    }

    function onRowSelect(rows: ElectronicContractModel[]) {
        setSelectedRows([...rows]);
    }

    const deleteSelected = async () => {
        setDeleting(true);
        const result = await electronicContractRepo.deleteMany(selectedRows);
        messageApi.success({
            content: `Bạn đã xoá thành công ${result.success}/${result.total} bản ghi`,
        });
        setDeleting(false);
    };

    const handleViewDocument = (record: ElectronicContractModel) => {
        if (!record?.id) {
            messageApi.warning({
                content: "Hợp đồng chưa được tạo"
            });
            return;
        }
        setModalData(record);
        setViewDocumentModal(true);
    };

    const handleSendOtp = (record: ElectronicContractModel) => {
        if (!record?.id) {
            messageApi.warning({
                content: "Hợp đồng chưa được tạo"
            });
            return;
        }
        setModalData(record);
        setSendOtpModal(true);
    };

    const handleSignDocument = (record: ElectronicContractModel) => {
        if (!record?.id) {
            messageApi.warning({
                content: "Hợp đồng chưa được tạo"
            });
            return;
        }
        setModalData(record);
        setSignDocumentModal(true);
    };

    const getStatusTag = (status?: string) => {
        if (!status) return <Tag color="default">Chưa xác định</Tag>;
        switch (status.toLowerCase()) {
            case "signed":
            case "đã ký":
                return <Tag color="success">Đã ký</Tag>;
            case "pending":
            case "chờ ký":
                return <Tag color="warning">Chờ ký</Tag>;
            case "draft":
            case "nháp":
                return <Tag color="default">Nháp</Tag>;
            default:
                return <Tag color="default">{status}</Tag>;
        }
    };

    const refreshContract = async () => {
        electronicContractRepo.refresh();
    };

    return (
        <div className="px-4 py-6">
            {contextHolder}
            <FilterBar
                setMap={setFilterMap}
                onChange={onFilterChange}
                templates={filterTemplates}
                map={filterMap}
                actions={[
                    <div key={1} className="col-span-4">
                        <DeleteButton
                            loading={deleting}
                            length={selectedRows.length}
                            onConfirm={deleteSelected}
                        />
                    </div>,
                ]}
            />
            <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
                <div>
                    <DataTable<ElectronicContractModel>
                        columns={[
                            { dataIndex: "waterUserCode", name: "Mã khách hàng" },
                            { dataIndex: "customerCode", name: "Mã KH (nội bộ)" },
                            { dataIndex: "customerName", name: "Tên khách hàng" },
                            { dataIndex: "customerPhone", name: "Số điện thoại" },
                            { dataIndex: "customerEmail", name: "Email" },
                            { dataIndex: "documentCode", name: "Mã hợp đồng" },
                            {
                                dataIndex: "status",
                                name: "Trạng thái",
                                render: (_, record) => getStatusTag(record.status),
                                fixed: "right",
                            },
                            {
                                dataIndex: "otpSentAt",
                                name: "Ngày gửi OTP",
                                pipe: formatDateTimePipe,
                            },
                            {
                                dataIndex: "signedAt",
                                name: "Ngày ký",
                                pipe: formatDateTimePipe,
                            },
                            {
                                dataIndex: "createdAt",
                                name: "Ngày tạo",
                                pipe: formatDateTimePipe,
                            },
                            {
                                name: "Thao tác",
                                render: (_, record) => (
                                    <div className="flex flex-row px-1 items-center justify-center">
                                        {/* <Tooltip title="Xem chi tiết">
                                            <Button
                                                type="text"
                                                icon={<InfoCircleOutlined />}
                                                onClick={() => showDetail(record)}
                                            />
                                        </Tooltip> */}
                                        <Dropdown
                                            menu={{
                                                items: [
                                                    {
                                                        key: "1",
                                                        label: <p>Xem tài liệu</p>,
                                                        icon: <EyeOutlined />,
                                                        onClick: () => handleViewDocument(record),
                                                    },
                                                    ...(record.status?.toLowerCase() !== "signed" && record.status?.toLowerCase() !== "đã ký" ? [
                                                        // {
                                                        //     key: "2",
                                                        //     label: <p>Gửi OTP</p>,
                                                        //     icon: <SafetyOutlined />,
                                                        //     onClick: () => handleSendOtp(record),
                                                        // },
                                                        // {
                                                        //     key: "3",
                                                        //     label: <p>Ký hợp đồng</p>,
                                                        //     icon: <CheckCircleOutlined />,
                                                        //     onClick: () => handleSignDocument(record),
                                                        // },
                                                    ] : []),
                                                ],
                                            }}
                                            trigger={["hover"]}
                                        >
                                            <Button type="text" icon={<MoreOutlined />} />
                                        </Dropdown>
                                    </div>
                                ),
                                fixed: "right",
                            },
                        ]}
                        repository={electronicContractRepo}
                        filterMap={filterMap}
                        onSelect={onRowSelect}
                    ></DataTable>
                </div>
            </div>
            <DetailModal
                isModalOpen={isDetailModal}
                setIsModalOpen={setDetailModal}
                data={modalData}
                waterUserCode={modalData.waterUserCode || modalData.customerCode || ""}
                onSuccess={refreshContract}
            />
            <CreateElectronicContractMultiModal
                isModalOpen={isCreateMultiModal}
                setIsModalOpen={setCreateMultiModal}
                query={{}}
            />
            {modalData?.id && (
                <>
                    <ViewDocumentModal
                        isModalOpen={isViewDocumentModal}
                        setIsModalOpen={setViewDocumentModal}
                        contractId={modalData.id}
                    />
                    <SendOtpModal
                        isModalOpen={isSendOtpModal}
                        setIsModalOpen={setSendOtpModal}
                        contractId={modalData.id}
                        waterUserCode={modalData.waterUserCode || modalData.customerCode || ""}
                    />
                    <SignDocumentModal
                        isModalOpen={isSignDocumentModal}
                        setIsModalOpen={setSignDocumentModal}
                        contractId={modalData.id}
                        onSuccess={refreshContract}
                    />
                </>
            )}
        </div>
    );
}

export default function ElectronicContractPage() {
    return (
        <Suspense>
            <ElectronicContract />
        </Suspense>
    );
}
